"use client";
import Image from 'next/image';
import { useMemo, useState } from 'react';
import Search from '@/components/Search/Search';
import { IPageContent } from "@/types/pageContent.types";
import BlogsSlider from "./BlogsSlider/BlogsSlider";
import styles from './styles.module.scss';


export interface IBlogValue {
  overline: string;
  blogArticleImage: {
    alt: string;
    url: string;
  };
  blogArticleDate: string;
  blogArticleTitleMetadata: string;
  blogArticleDescMetadata: string;
  blogArticleButton: string;
  heading: string;
  blogLink: string;
  body: string;
  nextBlogLink: string;
}

export interface IBlogProps {
  insightsPageHeadingModel: {
    insightsListingPageHeading: string;
    insightsListingPageFeatured: {
      overline: string;
      heading: string;
      button: string;
      gallery: {
        url: string;
        alt: string;
      }[];
    }[];
  };
  allInsightsListingPageModels: {
    blogPage: IBlogValue[];
  }[];
}




const BlogListing = ({ 
  // insightsPageHeadingModel, 
  allInsightsListingPageModels 
}: IBlogProps) => {
  // console.log('insightsPageHeadingModel', insightsPageHeadingModel);

  const [blogPosts, setBlogPosts] = useState<IBlogValue[] | null>(null);
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest" | "az" | "za">("newest");

  const parseDate = (dateStr: string) => {
    const [month, day, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  };


  const handleSearch = (query: string) => {
    const allBlogs = allInsightsListingPageModels.flatMap((page: { blogPage: IBlogValue[] }) => page.blogPage);

    const filteredResults = allBlogs.filter((blog: IBlogValue) =>
      blog.heading.toLowerCase().includes(query.toLowerCase()) ||
      blog.body.toLowerCase().includes(query.toLowerCase())
    );

    const sortedResults = filteredResults.sort((a: IBlogValue, b: IBlogValue) =>
      parseDate(b.blogArticleDate).getTime() - parseDate(a.blogArticleDate).getTime()
    );

    setBlogPosts(sortedResults);
  };

  const sortBlogs = (blogs: IBlogValue[], order: "newest" | "oldest" | "az" | "za") => {
    const sorted = [...blogs];
    sorted.sort((a, b) => {
      if (order === "newest") return parseDate(b.blogArticleDate).getTime() - parseDate(a.blogArticleDate).getTime();
      if (order === "oldest") return parseDate(a.blogArticleDate).getTime() - parseDate(b.blogArticleDate).getTime();
      if (order === "az") return a.heading.localeCompare(b.heading);
      return b.heading.localeCompare(a.heading);
    });
    return sorted;
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as "newest" | "oldest" | "az" | "za";
    setSortOrder(value);
  };


  const allSortedBlogs = [...allInsightsListingPageModels].sort((a: { blogPage: IBlogValue[] }, b: { blogPage: IBlogValue[] }) => {
    const dateA = parseDate(a.blogPage[0].blogArticleDate);
    const dateB = parseDate(b.blogPage[0].blogArticleDate);
    return dateB.getTime() - dateA.getTime();
  });

  const blogsToDisplay = blogPosts ?? allSortedBlogs.flatMap((model: { blogPage: IBlogValue[] }) => model.blogPage);
  const sortedBlogsToDisplay = useMemo(() => sortBlogs(blogsToDisplay, sortOrder), [blogsToDisplay, sortOrder]);

  // console.log('Insights blogsToDisplay', blogsToDisplay);

  return (
    <div className={`${styles.blogsListing} blogsListing`}>
      <div className={`${styles.container} maxWidthContainer`}>
        <h1>Insights</h1>

        <div className={styles.searchSortRow}>
          <Search onSearch={handleSearch} />

          <div className={styles.sortControl}>
            <label htmlFor="blogSort">Sort by:</label>
            <select
              id="blogSort"
              value={sortOrder}
              onChange={handleSortChange}
              className={styles.customSelect}
            >
              <option value="newest">Newest to Oldest</option>
              <option value="oldest">Oldest to Newest</option>
              <option value="az">A-Z</option>
              <option value="za">Z-A</option>
            </select>
          </div>
        </div>

        {/* <section className={styles.header}>
          <h1>{insightsPageHeadingModel.insightsListingPageHeading}</h1>
        </section>

        <section className={styles.blogFeaturedContainer}>
          <div className={styles.copyContainer}>
            <span className={styles.overline}>
              {insightsPageHeadingModel.insightsListingPageFeatured[0].overline}
            </span>
            <h2>
              {insightsPageHeadingModel.insightsListingPageFeatured[0].heading}
            </h2>
            <button>
              <p>{insightsPageHeadingModel.insightsListingPageFeatured[0].button}</p>
            </button>
          </div>

          <div className={styles.imgContainer}>
            <Image 
              src={insightsPageHeadingModel.insightsListingPageFeatured[0].gallery[0].url} 
              alt={insightsPageHeadingModel.insightsListingPageFeatured[0].gallery[0].alt} 
              width={4000}
              height={4000}
              quality={100}
            />
          </div>
        </section> */}

        <BlogsSlider
          section="BlogsListingFeatured"
          blogsListingFeaturedData={blogsToDisplay}
        />

        <div className={styles.blogsContent}>

          <ul className={styles.blogCards}>
            {sortedBlogsToDisplay.map((blog, index) => (
              <li key={`blog-${index}`} className={styles.blogCard}>
                <a href={blog.blogLink} className={styles.blogImageLink}>
                  {blog.blogArticleImage && blog.blogArticleImage.url ? (
                    <Image 
                      src={blog.blogArticleImage.url} 
                      alt={blog.blogArticleImage.alt || "Blog image"} 
                      width={375}
                      height={325}
                      className={styles.blogImage}
                      quality={75}
                    />
                  ) : (
                    <div className={styles.blogImagePlaceholder} />
                  )}
                </a>
                <span className={styles.blogDate}>{blog.blogArticleDate}</span>
                <a href={blog.blogLink} className={styles.blogTitleLink}>
                  <p className={styles.blogTitle}>{blog.heading}</p>
                </a>
                <a href={blog.blogLink} className={styles.readMore}>
                  {blog.blogArticleButton}
                  <span className="sr-only">about {blog.heading}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BlogListing;
